import { useEffect, useMemo, useState } from "react";

const PAGE_SIZE = 100;

export const useSummaryTable = ({
  activeMetricId,
  selectedAccount,
  selectedMarketplace,
  authFetch,
  tableCache,
  setTableCache,
}) => {
  const [tableSearch, setTableSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingTable, setLoadingTable] = useState(false);
  const [tableError, setTableError] = useState(null);

  const tableCacheKey = useMemo(() => {
    return `${selectedAccount}__${selectedMarketplace}__${activeMetricId}__${currentPage}__${appliedSearch}`;
  }, [
    selectedAccount,
    selectedMarketplace,
    activeMetricId,
    currentPage,
    appliedSearch,
  ]);

  useEffect(() => {
    const loadTable = async () => {
      if (!selectedAccount || !selectedMarketplace || !activeMetricId) {
        setLoadingTable(false);
        setTableError(null);
        return;
      }

      const isInitialDefaultTable = currentPage === 1 && appliedSearch === "";
      if (isInitialDefaultTable) {
        setLoadingTable(false);
        setTableError(null);
        return;
      }

      if (tableCache[tableCacheKey]) {
        setLoadingTable(false);
        setTableError(null);
        return;
      }

      try {
        setLoadingTable(true);
        setTableError(null);

        const response = await authFetch(
          `/amz-api/dashboard/detail/${activeMetricId}`,
          {
            method: "POST",
            body: JSON.stringify({
              account: selectedAccount,
              marketplace: selectedMarketplace,
              page: currentPage,
              pageSize: PAGE_SIZE,
              search: appliedSearch,
              tableOnly: true,
            }),
          }
        );

        if (!response) return;

        const data = await response.json();
        const tablePayload = data.table || data;

        setTableCache((prev) => ({
          ...prev,
          [tableCacheKey]: tablePayload,
        }));
      } catch (error) {
        setTableError("Failed to load table data");
      } finally {
        setLoadingTable(false);
      }
    };

    loadTable();
  }, [
    activeMetricId,
    currentPage,
    appliedSearch,
    selectedAccount,
    selectedMarketplace,
    tableCacheKey,
    authFetch,
    tableCache,
    setTableCache,
  ]);

  return {
    tableSearch,
    setTableSearch,
    appliedSearch,
    setAppliedSearch,
    currentPage,
    setCurrentPage,
    loadingTable,
    tableError,
    tableCacheKey,
  };  

};
